import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { contactInfo, navItems } from "@/mocks/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = !isHome || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  const muted = solid ? "text-foreground-700" : "text-background-100";
  const isActive = (href: string) =>
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href);

  return (
    <header
      className={`fixed left-0 top-0 z-50 w-full transition-all duration-300 ${
        solid ? "bg-background-50 border-b border-background-200" : "bg-transparent"
      }`}
    >
      {/* Top utility bar */}
      <div className={`hidden md:block border-b ${solid ? "border-background-200" : "border-white/15"}`}>
        <div className="mx-auto flex h-9 max-w-screen-2xl items-center justify-between px-4 md:px-6 text-xs">
          <div className={`flex items-center gap-5 ${muted}`}>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-sm" /> {contactInfo.hoursWeek}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-customer-service-2-line text-sm" /> {contactInfo.support}
            </span>
          </div>
          <div className={`flex items-center gap-5 ${muted}`}>
            <a href={contactInfo.emailHref} className="flex items-center gap-1.5 hover:text-primary-500 transition-colors">
              <i className="ri-mail-line text-sm" /> {contactInfo.email}
            </a>
            <a href={contactInfo.phoneHref} className="flex items-center gap-1.5 hover:text-primary-500 transition-colors">
              <i className="ri-phone-line text-sm" /> {contactInfo.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between gap-4 px-4 md:h-[72px] md:px-6">
        <Link to="/" className="flex shrink-0 items-center" aria-label="Lahari Packers and Movers Bangalore home">
          <span className="flex items-center rounded-lg bg-background-50 px-2.5 py-1.5">
            <img
              src={contactInfo.logo}
              alt="Lahari Packers and Movers Bangalore logo"
              title="Lahari Packers and Movers Bangalore"
              className="h-8 w-auto md:h-9"
            />
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <div key={item.label} className="group relative">
              <Link
                to={item.href}
                className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? "text-primary-500"
                    : solid
                      ? "text-foreground-800 hover:text-primary-500"
                      : "text-background-50 hover:text-primary-200"
                }`}
              >
                {item.label}
                {item.children && <i className="ri-arrow-down-s-line text-base" />}
              </Link>
              {item.children && (
                <div className="invisible absolute left-0 top-full z-50 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="min-w-[280px] rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className="block px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Link
            to="/request-a-quote"
            data-cta="Get A Quote"
            data-cta-location="navbar-desktop"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:block"
          >
            Get A Quote
          </Link>
          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            className={`flex h-10 w-10 items-center justify-center rounded-md transition-colors lg:hidden ${
              solid ? "text-foreground-950 hover:bg-background-100" : "text-background-50 hover:bg-white/10"
            }`}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-3-line"} text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 lg:hidden">
          <nav className="mx-auto max-w-screen-2xl px-4 py-3 md:px-6">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-background-100 last:border-0">
                <div className="flex items-center justify-between">
                  <Link to={item.href} className="flex-1 py-3 text-sm font-medium text-foreground-800">
                    {item.label}
                  </Link>
                  {item.children && (
                    <button
                      type="button"
                      aria-label={`Toggle ${item.label}`}
                      onClick={() => setOpenGroup((g) => (g === item.label ? null : item.label))}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100"
                    >
                      <i className={`${openGroup === item.label ? "ri-subtract-line" : "ri-add-line"} text-lg`} />
                    </button>
                  )}
                </div>
                {item.children && openGroup === item.label && (
                  <div className="pb-2 pl-4">
                    {item.children.map((child) => (
                      <Link key={child.label} to={child.href} className="block py-2 text-sm text-foreground-600">
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-4 flex flex-col gap-2">
              <a
                href={contactInfo.phoneHref}
                className="flex items-center justify-center gap-2 rounded-md border border-primary-300 py-3 text-sm font-semibold text-primary-600"
              >
                <i className="ri-phone-line" /> Call {contactInfo.phone}
              </a>
              <Link
                to="/request-a-quote"
                data-cta="Get A Quote"
                data-cta-location="navbar-mobile"
                className="flex items-center justify-center gap-2 rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50"
              >
                <i className="ri-file-text-line" /> Get A Quote
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}